import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GeneratedSite, GeneratedBlock } from '@/lib/siteGenerator';

// Types
export interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  timestamp: Date;
  suggestions?: string[];
}

export interface ConversationState {
  step: 'intro' | 'collecting' | 'generating' | 'preview' | 'publishing' | 'done';
  businessName?: string;
  businessType?: string;
  description?: string;
  services?: string[];
  style?: string;
  contact?: {
    phone?: string;
    email?: string;
    address?: string;
  };
}

export type GenerationPhase =
  | 'idle'
  | 'analyzing'
  | 'structuring'
  | 'designing'
  | 'generating'
  | 'optimizing'
  | 'finalizing';

export type PreviewMode = 'desktop' | 'tablet' | 'mobile' | 'custom';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Panel types - all available panels in the assistant
export type PanelType =
  // Core panels
  | 'style' | 'seo' | 'templates' | 'blockPresets' | 'analytics'
  | 'history' | 'language' | 'export' | 'summary' | 'quickTips'
  // Design panels
  | 'colorPalette' | 'colorGenerator' | 'fontPairing' | 'brandKit'
  | 'layoutSuggestions' | 'colorContrast'
  // Content panels
  | 'contentSuggestions' | 'testimonialGenerator' | 'faqGenerator'
  | 'blogPreview' | 'blogManager' | 'emailTemplate' | 'socialMedia'
  // SEO & Performance panels
  | 'seoAnalyzer' | 'pageSpeed' | 'accessibility' | 'metaTags'
  | 'schemaMarkup' | 'sitemapPreview'
  // Widget panels
  | 'chatbot' | 'whatsapp' | 'googleMaps' | 'videoEmbed' | 'countdown'
  | 'newsletter' | 'imageGallery' | 'socialProof' | 'popup' | 'scrollTop'
  | 'breadcrumbs' | 'stickyHeader' | 'progressBar' | 'animatedCounter'
  // Business panels
  | 'businessHours' | 'pricingTable' | 'cookieConsent' | 'contactForm'
  | 'legalPages' | 'testimonials' | 'team' | 'portfolio' | 'processSteps'
  // Component panels
  | 'heroSection' | 'footerBuilder' | 'ctaBanner' | 'serviceCards'
  | 'accordion' | 'tabs' | 'testimonialsWall' | 'logoCloud' | 'featureGrid'
  | 'statsGrid' | 'timeline' | 'beforeAfter' | 'tableGenerator'
  // Advanced panels
  | 'customCss' | 'iconLibrary' | 'animationLibrary' | 'integrationWidgets'
  | 'responsiveBreakpoints' | 'devicePreview' | 'blockTemplates'
  | 'templateGallery' | 'aiSuggestions' | 'imageOptimizer' | 'qrCode'
  | 'competitorAnalysis' | 'abTesting' | 'siteBackup' | 'contentCalendar'
  | 'socialPreview' | 'comparison' | 'quickBlockEditor' | 'saveAsTemplate'
  // New VibeSdk-inspired panels
  | 'monacoEditor' | 'githubExport' | 'agentChat' | 'generationPhases'
  // Professional panels
  | 'ecommerce' | 'analyticsDashboard' | 'membership' | 'apiIntegration'
  | 'versioning' | 'performance' | 'workflowAutomation' | 'multiSite'
  | 'advancedSeo' | 'security' | 'notificationCenter'
  | 'advancedForms' | 'teamRoles' | 'emailMarketing' | 'domainManager';

// Main store interface
interface AssistantState {
  // Conversation
  messages: Message[];
  conversationState: ConversationState;
  isTyping: boolean;
  inputValue: string;

  // Generation
  generationPhase: GenerationPhase;
  generationProgress: number;
  generationMessage: string;
  isGenerating: boolean;
  generatedSite: GeneratedSite | null;

  // Panels (replaces 100+ useState)
  activePanels: Set<PanelType>;

  // Preview
  previewMode: PreviewMode;
  previewWidth: number;
  previewZoom: number;
  isFullscreenPreview: boolean;
  previewKey: number;
  showBlockList: boolean;
  selectedBlockIndex: number | undefined;

  // History (undo/redo)
  siteHistory: GeneratedSite[];
  historyIndex: number;
  maxHistorySize: number;

  // Block operations
  copiedBlock: GeneratedBlock | null;

  // Session
  saveStatus: SaveStatus;
  lastSavedAt: Date | null;
  hasRestoredSession: boolean;

  // Publishing
  isPublishing: boolean;
  publishedSiteId: string | null;
  publishedUrl: string | null;

  // UI
  chatPanelWidth: number;
  showPreview: boolean;
}

// Actions interface
interface AssistantActions {
  // Messages
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;
  setInputValue: (value: string) => void;
  setIsTyping: (isTyping: boolean) => void;

  // Conversation
  setConversationState: (state: Partial<ConversationState>) => void;
  setConversationStep: (step: ConversationState['step']) => void;

  // Generation
  setGenerationPhase: (phase: GenerationPhase) => void;
  setGenerationProgress: (progress: number, message?: string) => void;
  setGenerationMessage: (message: string) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setGeneratedSite: (site: GeneratedSite | null) => void;
  updateSiteSettings: (settings: Partial<GeneratedSite['settings']>) => void;

  // Panels
  openPanel: (panel: PanelType) => void;
  closePanel: (panel: PanelType) => void;
  togglePanel: (panel: PanelType) => void;
  closeAllPanels: () => void;
  isPanelOpen: (panel: PanelType) => boolean;

  // Preview
  setPreviewMode: (mode: PreviewMode) => void;
  setPreviewWidth: (width: number) => void;
  setPreviewZoom: (zoom: number) => void;
  toggleFullscreenPreview: () => void;
  refreshPreview: () => void;
  setShowBlockList: (show: boolean) => void;
  setSelectedBlockIndex: (index: number | undefined) => void;

  // History
  pushToHistory: (site: GeneratedSite) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  jumpToHistory: (index: number) => void;

  // Block operations
  updateBlock: (pageIndex: number, blockIndex: number, content: Record<string, unknown>) => void;
  addBlock: (pageIndex: number, blockType: string, afterIndex?: number) => void;
  removeBlock: (pageIndex: number, blockIndex: number) => void;
  moveBlock: (pageIndex: number, blockIndex: number, direction: 'up' | 'down') => void;
  duplicateBlock: (pageIndex: number, blockIndex: number) => void;
  copyBlock: (pageIndex: number, blockIndex: number) => void;
  pasteBlock: (pageIndex: number, afterIndex: number) => void;
  toggleBlockVisibility: (pageIndex: number, blockIndex: number) => void;

  // Session
  setSaveStatus: (status: SaveStatus) => void;
  setHasRestoredSession: (restored: boolean) => void;
  saveSession: () => void;
  restoreSession: () => boolean;
  clearSession: () => void;

  // Publishing
  setIsPublishing: (isPublishing: boolean) => void;
  setPublishedSite: (siteId: string | null, url: string | null) => void;

  // UI
  setChatPanelWidth: (width: number) => void;
  setShowPreview: (show: boolean) => void;

  // Reset
  reset: () => void;
}

// Initial state
const initialState: AssistantState = {
  // Conversation
  messages: [],
  conversationState: { step: 'intro' },
  isTyping: false,
  inputValue: '',

  // Generation
  generationPhase: 'idle',
  generationProgress: 0,
  generationMessage: '',
  isGenerating: false,
  generatedSite: null,

  // Panels
  activePanels: new Set<PanelType>(),

  // Preview
  previewMode: 'desktop',
  previewWidth: 1200,
  previewZoom: 100,
  isFullscreenPreview: false,
  previewKey: 0,
  showBlockList: false,
  selectedBlockIndex: undefined,

  // History
  siteHistory: [],
  historyIndex: -1,
  maxHistorySize: 50,

  // Block operations
  copiedBlock: null,

  // Session
  saveStatus: 'idle',
  lastSavedAt: null,
  hasRestoredSession: false,

  // Publishing
  isPublishing: false,
  publishedSiteId: null,
  publishedUrl: null,

  // UI
  chatPanelWidth: 50,
  showPreview: true,
};

// Storage keys
const STORAGE_KEYS = {
  SITE: 'mawkaa_assistant_site',
  CONVERSATION: 'mawkaa_assistant_conversation',
  STATE: 'mawkaa_assistant_state',
} as const;

// Create the store
export const useAssistantStore = create<AssistantState & AssistantActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Messages
      addMessage: (message) => {
        const newMessage: Message = {
          ...message,
          id: crypto.randomUUID(),
          timestamp: new Date(),
        };
        set((state) => ({
          messages: [...state.messages, newMessage],
        }));
      },

      clearMessages: () => set({ messages: [] }),

      setInputValue: (value) => set({ inputValue: value }),

      setIsTyping: (isTyping) => set({ isTyping }),

      // Conversation
      setConversationState: (newState) =>
        set((state) => ({
          conversationState: { ...state.conversationState, ...newState },
        })),

      setConversationStep: (step) =>
        set((state) => ({
          conversationState: { ...state.conversationState, step },
        })),

      // Generation
      setGenerationPhase: (phase) => set({ generationPhase: phase }),

      setGenerationProgress: (progress, message) =>
        set({
          generationProgress: progress,
          generationMessage: message ?? '',
        }),

      setGenerationMessage: (message) => set({ generationMessage: message }),

      setIsGenerating: (isGenerating) => set({ isGenerating }),

      setGeneratedSite: (site) => {
        set({ generatedSite: site });
        if (site) {
          get().pushToHistory(site);
        }
      },

      updateSiteSettings: (settings) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const updatedSite = {
            ...state.generatedSite,
            settings: { ...state.generatedSite.settings, ...settings },
          };
          return { generatedSite: updatedSite };
        }),

      // Panels
      openPanel: (panel) =>
        set((state) => {
          const newPanels = new Set(state.activePanels);
          newPanels.add(panel);
          return { activePanels: newPanels };
        }),

      closePanel: (panel) =>
        set((state) => {
          const newPanels = new Set(state.activePanels);
          newPanels.delete(panel);
          return { activePanels: newPanels };
        }),

      togglePanel: (panel) => {
        const { activePanels, openPanel, closePanel } = get();
        if (activePanels.has(panel)) {
          closePanel(panel);
        } else {
          openPanel(panel);
        }
      },

      closeAllPanels: () => set({ activePanels: new Set() }),

      isPanelOpen: (panel) => get().activePanels.has(panel),

      // Preview
      setPreviewMode: (mode) => set({ previewMode: mode }),

      setPreviewWidth: (width) => set({ previewWidth: width }),

      setPreviewZoom: (zoom) => set({ previewZoom: zoom }),

      toggleFullscreenPreview: () =>
        set((state) => ({ isFullscreenPreview: !state.isFullscreenPreview })),

      refreshPreview: () =>
        set((state) => ({ previewKey: state.previewKey + 1 })),

      setShowBlockList: (show) => set({ showBlockList: show }),

      setSelectedBlockIndex: (index) => set({ selectedBlockIndex: index }),

      // History
      pushToHistory: (site) =>
        set((state) => {
          const newHistory = state.siteHistory.slice(0, state.historyIndex + 1);
          newHistory.push(JSON.parse(JSON.stringify(site)));
          if (newHistory.length > state.maxHistorySize) {
            newHistory.shift();
          }
          return {
            siteHistory: newHistory,
            historyIndex: newHistory.length - 1,
          };
        }),

      undo: () => {
        const { historyIndex, siteHistory } = get();
        if (historyIndex > 0) {
          const newIndex = historyIndex - 1;
          set({
            historyIndex: newIndex,
            generatedSite: JSON.parse(JSON.stringify(siteHistory[newIndex])),
          });
        }
      },

      redo: () => {
        const { historyIndex, siteHistory } = get();
        if (historyIndex < siteHistory.length - 1) {
          const newIndex = historyIndex + 1;
          set({
            historyIndex: newIndex,
            generatedSite: JSON.parse(JSON.stringify(siteHistory[newIndex])),
          });
        }
      },

      canUndo: () => get().historyIndex > 0,

      canRedo: () => {
        const { historyIndex, siteHistory } = get();
        return historyIndex < siteHistory.length - 1;
      },

      jumpToHistory: (index) => {
        const { siteHistory } = get();
        if (index >= 0 && index < siteHistory.length) {
          set({
            historyIndex: index,
            generatedSite: JSON.parse(JSON.stringify(siteHistory[index])),
          });
        }
      },

      // Block operations
      updateBlock: (pageIndex, blockIndex, content) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          if (site.pages[pageIndex]?.blocks[blockIndex]) {
            site.pages[pageIndex].blocks[blockIndex].content = {
              ...site.pages[pageIndex].blocks[blockIndex].content,
              ...content,
            };
          }
          get().pushToHistory(site);
          return { generatedSite: site };
        }),

      addBlock: (pageIndex, blockType, afterIndex) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          const newBlock: GeneratedBlock = {
            type: blockType,
            content: {},
            settings: {},
          };
          const insertIndex = afterIndex !== undefined ? afterIndex + 1 : site.pages[pageIndex].blocks.length;
          site.pages[pageIndex].blocks.splice(insertIndex, 0, newBlock);
          get().pushToHistory(site);
          return { generatedSite: site };
        }),

      removeBlock: (pageIndex, blockIndex) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          site.pages[pageIndex].blocks.splice(blockIndex, 1);
          get().pushToHistory(site);
          return { generatedSite: site };
        }),

      moveBlock: (pageIndex, blockIndex, direction) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          const blocks = site.pages[pageIndex].blocks;
          const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
          if (newIndex >= 0 && newIndex < blocks.length) {
            [blocks[blockIndex], blocks[newIndex]] = [blocks[newIndex], blocks[blockIndex]];
            get().pushToHistory(site);
            return { generatedSite: site, selectedBlockIndex: newIndex };
          }
          return state;
        }),

      duplicateBlock: (pageIndex, blockIndex) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          const block = site.pages[pageIndex].blocks[blockIndex];
          const duplicate = JSON.parse(JSON.stringify(block));
          site.pages[pageIndex].blocks.splice(blockIndex + 1, 0, duplicate);
          get().pushToHistory(site);
          return { generatedSite: site };
        }),

      copyBlock: (pageIndex, blockIndex) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const block = state.generatedSite.pages[pageIndex]?.blocks[blockIndex];
          if (block) {
            return { copiedBlock: JSON.parse(JSON.stringify(block)) };
          }
          return state;
        }),

      pasteBlock: (pageIndex, afterIndex) =>
        set((state) => {
          if (!state.generatedSite || !state.copiedBlock) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          const newBlock = JSON.parse(JSON.stringify(state.copiedBlock));
          site.pages[pageIndex].blocks.splice(afterIndex + 1, 0, newBlock);
          get().pushToHistory(site);
          return { generatedSite: site };
        }),

      toggleBlockVisibility: (pageIndex, blockIndex) =>
        set((state) => {
          if (!state.generatedSite) return state;
          const site = JSON.parse(JSON.stringify(state.generatedSite));
          const block = site.pages[pageIndex]?.blocks[blockIndex];
          if (block) {
            block.settings = block.settings || {};
            block.settings.hidden = !block.settings.hidden;
            get().pushToHistory(site);
            return { generatedSite: site };
          }
          return state;
        }),

      // Session
      setSaveStatus: (status) =>
        set({
          saveStatus: status,
          lastSavedAt: status === 'saved' ? new Date() : get().lastSavedAt,
        }),

      setHasRestoredSession: (restored) => set({ hasRestoredSession: restored }),

      saveSession: () => {
        const { generatedSite, conversationState, messages } = get();
        try {
          if (generatedSite) {
            localStorage.setItem(STORAGE_KEYS.SITE, JSON.stringify(generatedSite));
          }
          localStorage.setItem(STORAGE_KEYS.STATE, JSON.stringify(conversationState));
          localStorage.setItem(STORAGE_KEYS.CONVERSATION, JSON.stringify(messages));
          set({ saveStatus: 'saved', lastSavedAt: new Date() });
        } catch {
          set({ saveStatus: 'error' });
        }
      },

      restoreSession: () => {
        try {
          const savedSite = localStorage.getItem(STORAGE_KEYS.SITE);
          const savedState = localStorage.getItem(STORAGE_KEYS.STATE);
          const savedMessages = localStorage.getItem(STORAGE_KEYS.CONVERSATION);

          if (savedSite && savedState) {
            const site = JSON.parse(savedSite);
            const state = JSON.parse(savedState);
            const messages = savedMessages ? JSON.parse(savedMessages) : [];

            set({
              generatedSite: site,
              conversationState: state,
              messages,
              hasRestoredSession: true,
            });
            return true;
          }
        } catch {
          // Ignore parse errors
        }
        return false;
      },

      clearSession: () => {
        localStorage.removeItem(STORAGE_KEYS.SITE);
        localStorage.removeItem(STORAGE_KEYS.STATE);
        localStorage.removeItem(STORAGE_KEYS.CONVERSATION);
        set({
          generatedSite: null,
          conversationState: { step: 'intro' },
          messages: [],
          siteHistory: [],
          historyIndex: -1,
        });
      },

      // Publishing
      setIsPublishing: (isPublishing) => set({ isPublishing }),

      setPublishedSite: (siteId, url) =>
        set({
          publishedSiteId: siteId,
          publishedUrl: url,
        }),

      // UI
      setChatPanelWidth: (width) => set({ chatPanelWidth: width }),

      setShowPreview: (show) => set({ showPreview: show }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: 'mawkaa-assistant-store',
      partialize: (state) => ({
        // Only persist essential state
        previewMode: state.previewMode,
        previewZoom: state.previewZoom,
        chatPanelWidth: state.chatPanelWidth,
        showBlockList: state.showBlockList,
      }),
    }
  )
);

// Selectors for common use cases
export const useMessages = () => useAssistantStore((state) => state.messages);
export const useConversationState = () => useAssistantStore((state) => state.conversationState);
export const useGeneratedSite = () => useAssistantStore((state) => state.generatedSite);
export const useGenerationPhase = () => useAssistantStore((state) => ({
  phase: state.generationPhase,
  progress: state.generationProgress,
  message: state.generationMessage,
  isGenerating: state.isGenerating,
}));
export const usePreviewSettings = () => useAssistantStore((state) => ({
  mode: state.previewMode,
  width: state.previewWidth,
  zoom: state.previewZoom,
  isFullscreen: state.isFullscreenPreview,
  key: state.previewKey,
}));
export const useHistoryState = () => useAssistantStore((state) => ({
  history: state.siteHistory,
  index: state.historyIndex,
  canUndo: state.historyIndex > 0,
  canRedo: state.historyIndex < state.siteHistory.length - 1,
}));

// Re-export types for convenience
export type { GeneratedSite, GeneratedBlock } from '@/lib/siteGenerator';
